import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {ScalarMathNodeOperation} from "#material/nodes/converter/scalar-math/constants"
import {scalarMathNodeOperation} from "#material/nodes/converter/scalar-math/constants"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"

export interface ScalarMathSockets extends MaterialNodeSockets {
    inputs: {
        value: "float" // found in inputs: color,float,vector; found in settings: float,scalar; inferred from data: float
        value_001: "float" // found in inputs: float,color; found in settings: float,scalar; inferred from data: float
        value_002: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        value: "float" // found in inputs: color,float,vector; found in settings: float,scalar; inferred from data: float
        value_001: "float" // found in inputs: float,color; found in settings: float,scalar; inferred from data: float
        value_002: "float" // found in inputs: -; found in settings: float; inferred from data: float
        useClamp: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
    }
    settingsExtensions: {
        operation?: ScalarMathNodeOperation // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        value: "float"
    }
}

const TWO_OPERANDS = {
    setting: "operation",
    oneOf: [
        undefined,
        "ADD",
        "SUBTRACT",
        "MULTIPLY",
        "DIVIDE",
        "MULTIPLY_ADD",
        "POWER",
        "LOGARITHM",
        "MINIMUM",
        "MAXIMUM",
        "LESS_THAN",
        "GREATER_THAN",
        "COMPARE",
        "SMOOTH_MIN",
        "SMOOTH_MAX",
        "MODULO",
        "FLOORED_MODULO",
        "WRAP",
        "SNAP",
        "PINGPONG",
        "ARCTAN2",
    ],
} as const

const THREE_OPERANDS = {setting: "operation", oneOf: ["MULTIPLY_ADD", "COMPARE", "SMOOTH_MIN", "SMOOTH_MAX", "WRAP"]} as const

export const scalarMathDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeMath,
    },
    nodeClass: "ScalarMath",
    ui: {
        label: "Math",
        color: "#4987af",
        items: [
            {name: "value", valueType: "float", label: "Value", output: true},
            {
                name: "value_001",
                valueType: "float",
                label: "Value",
                input: true,
                setting: true,
                defaultValue: 0.5,
                visibleWhen: TWO_OPERANDS,
            },
            {
                name: "value_002",
                valueType: "float",
                label: "Value",
                input: true,
                setting: true,
                defaultValue: 0,
                visibleWhen: THREE_OPERANDS,
            },
            {
                defaultValue: "ADD",
                name: "operation",
                label: "Operation",
                valueType: "string",
                options: [
                    {label: "Add", value: "ADD"},
                    {label: "Subtract", value: "SUBTRACT"},
                    {label: "Multiply", value: "MULTIPLY"},
                    {label: "Divide", value: "DIVIDE"},
                    {label: "Multiply Add", value: "MULTIPLY_ADD"},
                    {label: "Power", value: "POWER"},
                    {label: "Logarithm", value: "LOGARITHM"},
                    {label: "Square Root", value: "SQRT"},
                    {label: "Inverse Square Root", value: "INVERSE_SQRT"},
                    {label: "Absolute", value: "ABSOLUTE"},
                    {label: "Exponent", value: "EXPONENT"},
                    {label: "Minimum", value: "MINIMUM"},
                    {label: "Maximum", value: "MAXIMUM"},
                    {label: "Less Than", value: "LESS_THAN"},
                    {label: "Greater Than", value: "GREATER_THAN"},
                    {label: "Sign", value: "SIGN"},
                    {label: "Compare", value: "COMPARE"},
                    {label: "Smooth Minimum", value: "SMOOTH_MIN"},
                    {label: "Smooth Maximum", value: "SMOOTH_MAX"},
                    {label: "Round", value: "ROUND"},
                    {label: "Floor", value: "FLOOR"},
                    {label: "Ceil", value: "CEIL"},
                    {label: "Truncate", value: "TRUNC"},
                    {label: "Fraction", value: "FRACT"},
                    {label: "Truncated Modulo", value: "MODULO"},
                    {label: "Floored Modulo", value: "FLOORED_MODULO"},
                    {label: "Wrap", value: "WRAP"},
                    {label: "Snap", value: "SNAP"},
                    {label: "Ping-Pong", value: "PINGPONG"},
                    {label: "Sine", value: "SINE"},
                    {label: "Cosine", value: "COSINE"},
                    {label: "Tangent", value: "TANGENT"},
                    {label: "Arcsine", value: "ARCSINE"},
                    {label: "Arccosine", value: "ARCCOSINE"},
                    {label: "Arctangent", value: "ARCTANGENT"},
                    {label: "Arctan2", value: "ARCTAN2"},
                    {label: "Hyperbolic Sine", value: "SINH"},
                    {label: "Hyperbolic Cosine", value: "COSH"},
                    {label: "Hyperbolic Tangent", value: "TANH"},
                    {label: "To Radians", value: "RADIANS"},
                    {label: "To Degrees", value: "DEGREES"},
                ],
                input: false,
                setting: true,
            },
            {name: "useClamp", label: "Clamp", valueType: "boolean", defaultValue: false, input: false, setting: true},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({value: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                value: optionalMaterialInput,
                value_001: optionalMaterialInput,
                value_002: optionalMaterialInput,
            }),
            settings: z.strictObject({
                operation: z.optional(scalarMathNodeOperation),
                value: z.optional(z.number()),
                value_001: z.optional(z.number()),
                value_002: z.optional(z.number()), //used by "wrap"
                useClamp: z.optional(z.boolean()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<ScalarMathSockets>
